import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface UserType {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	name: string;
}

interface User {
	id: string;
	emailAddress: string;
	phoneNumber: string;
	firstName: string;
	lastName: string;
	isActive: boolean;
	isVerified: boolean;
	role?: string; // Optional since it might not be in API response
	userType?: UserType; // Optional since it might not be in API response
	// New fields for roles and permissions
	roles?: string[]; // Array of role identifiers from API
	permissions?: string[]; // Array of permission identifiers from API
	createdAt: string;
	updatedAt: string;
}

interface UserState {
	user: User | null;
	// role?: string; // Optional since it might not be in API response
	token: string | null;
	isAuthenticated: boolean;
	expireTimer: string | number;
	resetToken: string | null;
	organizationId: number | null;
	organizationName: string | null;
}

const initialState: UserState = {
	user: null,
	token: null,
	isAuthenticated: false,
	expireTimer: "",
	resetToken: null,
	organizationId: null,
	organizationName: null,
};

const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		setUser: (
			state,
			action: PayloadAction<{ user: User; token: string; organizationId?: number; organizationName?: string }>,
		) => {
			state.user = action.payload.user;
			state.token = action.payload.token;
			state.isAuthenticated = true;
			state.organizationId = action.payload.organizationId ?? null;
			state.organizationName = action.payload.organizationName ?? null;
		},
		clearUser: state => {
			state.user = null;
			state.token = null;
			state.isAuthenticated = false;
			state.organizationId = null;
			state.organizationName = null;
		},
		setExpireTimer: (state, action: PayloadAction<string>) => {
			state.expireTimer = action.payload;
		},
		setResetToken: (state, action: PayloadAction<string>) => {
			state.resetToken = action.payload;
		},
		initializeUser: (
			state,
			action: PayloadAction<{
				user: User | null;
				token: string | null;
				organizationId?: number | null;
				organizationName?: string | null;
			}>,
		) => {
			state.user = action.payload.user;
			state.token = action.payload.token;
			state.isAuthenticated = !!action.payload.token;
			state.organizationId = action.payload.organizationId ?? null;
			state.organizationName = action.payload.organizationName ?? null;
		},
	},
});

export const { setUser, clearUser, setExpireTimer, setResetToken, initializeUser } = userSlice.actions;
export default userSlice.reducer;
